/**
 * @voltxampere/presets — RL circuit (#5)
 *
 * V1 (DC) → R → L → gnd. At DC the inductor acts as a short, so
 * V(L_node) settles at 0 and the current through the chain is
 * V1/R. The DC test pins V(mid) = 0 — the inductor's DC short
 * invariant.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
// Sprint 25 R2 lane 4D-A — hand-authored layout. Source on the
// left, series resistor in the middle, shunt inductor rotated 90°
// on the right. Mirrors RC-LP topology with the cap swapped for an
// inductor — the visual rhyme matches the conceptual duality.
export const rlCircuit = {
    id: "rl-circuit",
    name: { tr: "RL Devresi", en: "RL Circuit" },
    category: "basic",
    difficulty: 2,
    description: {
        tr: "Seri RL devresi. V1=10V, R=100Ω, L=10mH, τ = L/R = 100µs. DC'de bobin kısa devredir.",
        en: "Series RL circuit. V1=10V, R=100Ω, L=10mH, τ = L/R = 100µs. DC: inductor shorts.",
    },
    circuit: makeCircuit({
        slot: 5,
        name: "RL Circuit",
        description: "Series RL circuit, τ = 100 µs",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "mid" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 100 },
                terminals: { p1: "net_vcc", p2: "net_mid" },
                label: "R1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "L1",
                type: "inductor",
                params: { inductance: 10e-3 },
                terminals: { p1: "net_mid", p2: "net_gnd" },
                label: "L1",
                position: { x: 160, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // vcc rail: V1.pos (-160, -10) → R1.p1 (-20, 0)
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -40 },
                    { x: -20, y: -40 },
                    { x: -20, y: 0 },
                ],
                style: "orthogonal",
            },
            // mid rail: R1.p2 (20, 0) → L1.p1 (160, -20)
            {
                id: "w_mid",
                netId: "net_mid",
                path: [
                    { x: 20, y: 0 },
                    { x: 160, y: 0 },
                    { x: 160, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd leg A: V1.neg (-160, 10) → GND.p (-160, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd leg B: L1.p2 (160, 20) → GND.p (-160, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 160, y: 20 },
                    { x: 160, y: 110 },
                    { x: -160, y: 110 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "mid",
            analysis: "dc",
            value: 0,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Bobin DC'de kısa devre, AC'de empedanslıdır (jωL).",
            en: "Inductor is a short at DC, has impedance jωL at AC.",
        },
        {
            tr: "Zaman sabiti τ = L/R bobin akımının değişim hızını belirler.",
            en: "Time constant τ = L/R sets the inductor current's rate of change.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=rl-circuit.js.map